// src/core/oneEuroFilter.js
//
// One-Euro Filter (Casiez et al., CHI 2012). The standard signal filter
// for taming jittery interactive signals such as face landmarks and
// head-pose estimates. Key property: the cutoff frequency adapts to the
// signal velocity, so it heavily smooths when the user is still and lets
// fast motion through with minimal lag.
//
// Usage:
//   const f = new OneEuroFilter({ mincutoff: 1.0, beta: 0.007, dcutoff: 1.0 });
//   const smoothed = f.filter(rawValue, timestampSeconds);
//
// Tuning:
//   mincutoff — lower values smooth more when the user is still (1.0 is a good start)
//   beta      — higher values reduce lag during fast motion (0.005–0.05 typical)
//
// For multi-dimensional values (vector, quaternion), use one filter per component.

export class OneEuroFilter {
  constructor({ mincutoff = 1.0, beta = 0.007, dcutoff = 1.0 } = {}) {
    this.mincutoff = mincutoff;
    this.beta = beta;
    this.dcutoff = dcutoff;
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }

  _alpha(cutoff, dt) {
    const tau = 1.0 / (2.0 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / dt);
  }

  filter(x, t) {
    if (this.tPrev === null) {
      this.tPrev = t;
      this.xPrev = x;
      return x;
    }
    const dt = Math.max(t - this.tPrev, 1e-6);
    this.tPrev = t;

    // 1. Estimate derivative
    const dx = (x - this.xPrev) / dt;
    const aD = this._alpha(this.dcutoff, dt);
    const dxHat = aD * dx + (1 - aD) * this.dxPrev;
    this.dxPrev = dxHat;

    // 2. Adaptive cutoff
    const cutoff = this.mincutoff + this.beta * Math.abs(dxHat);

    // 3. Smooth the signal
    const a = this._alpha(cutoff, dt);
    const xHat = a * x + (1 - a) * this.xPrev;
    this.xPrev = xHat;

    return xHat;
  }

  reset() {
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
}

// Convenience: vector filter (one OneEuro per component).
export class OneEuroVectorFilter {
  constructor(size, opts) {
    this.filters = Array.from({ length: size }, () => new OneEuroFilter(opts));
  }
  filter(arr, t) {
    return this.filters.map((f, i) => f.filter(arr[i], t));
  }
  reset() {
    this.filters.forEach(f => f.reset());
  }
}
